<template>
  <div>
    <Section
      :heading="`${type === 'edit' ? 'Edit' : 'Add'} Contact Settings`"
      :mode="mode"
    />
    <div class="grid gap-6 sm:gap-4">
      <AppCard
        :mode="mode"
        title="Name Settings"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="first-name"
              title="First name"
              placeholder="Not specified"
              v-model="contactData.first_name"
            />
            <AppInput
              :mode="mode"
              id="last-name"
              title="Last name"
              placeholder="Not specified"
              v-model="contactData.last_name"
            />
            <AppInput
              :mode="mode"
              id="title"
              title="Title"
              placeholder="Not necessarily"
              v-model="contactData.title"
            />
            <AppInput
              :mode="mode"
              id="username"
              title="Username"
              placeholder="Not specified"
              v-model="contactData.username"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Phone Numbers"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              type="tel"
              id="work-phone"
              title="Work phone"
              placeholder="Not specified"
              v-model="contactData.work_phone"
            />
            <AppInput
              :mode="mode"
              type="tel"
              id="home-phone"
              title="Home phone"
              placeholder="Not specified"
              v-model="contactData.home_phone"
            />
            <AppInput
              :mode="mode"
              type="tel"
              id="mobile-phone"
              title="Mobile phone"
              placeholder="Not specified"
              v-model="contactData.mobile_phone"
            />
            <AppInput
              :mode="mode"
              type="tel"
              id="fax"
              title="Fax"
              placeholder="Not specified"
              v-model="contactData.fax"
            />
            <AppInput
              :mode="mode"
              type="tel"
              id="other-phone"
              title="Other phone"
              placeholder="Not specified"
              v-model="contactData.other_phone"
            />
            <AppInput
              :mode="mode"
              id="toll"
              title="Toll free"
              placeholder="Not specified"
              v-model="memberData.toll_free_phone"
              :isDisabled="type === 'add'"
            />
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Address Information"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              id="address-1"
              title="Address 1"
              placeholder="Not specified"
              v-model="contactData.address1"
            />
            <AppInput
              :mode="mode"
              id="address-2"
              title="Address 2"
              placeholder="Not specified"
              v-model="contactData.address2"
            />
            <AppSelect
              :mode="mode"
              title="Country"
              v-model="contactData.country"
            >
              <template #options>
                <option value="">Country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                  :selected="country.code === contactData.country"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="zip"
              title="Zip"
              placeholder="Not specified"
              v-model="contactData.zip"
            />
            <AppSelect
              :mode="mode"
              title="City"
              v-model="contactData.city"
              :isDisabled="cities.length === 0"
            >
              <template #options>
                <option value="">City</option>
                <option
                  v-for="(city, key) in cities"
                  :value="city.name"
                  :key="key"
                  :selected="city.name === contactData.city"
                >
                  {{ city.name }}
                </option>
              </template>
            </AppSelect>
            <AppSelect
              :mode="mode"
              title="State"
              v-model="contactData.state"
              :isDisabled="states.length === 0"
            >
              <template #options>
                <option value="">State</option>
                <option
                  v-for="(state, key) in states"
                  :value="state.code"
                  :key="key"
                  :selected="state.code === contactData.state"
                >
                  {{ state.name }}
                </option>
              </template>
            </AppSelect>
          </div>
        </template>
      </AppCard>
      <AppCard
        :mode="mode"
        title="Other Communication Channels"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppInput
              :mode="mode"
              type="email"
              id="email"
              title="Email"
              placeholder="Not specified"
              v-model="contactData.email"
            />
            <AppInput
              :mode="mode"
              id="linkedin"
              title="Linkedin"
              placeholder="Not specified"
              v-model="contactData.linkedin_url"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppCheckbox
                :mode="mode"
                id="out-office"
                v-model="contactData.on_vacation"
                title="I am out of the office, do not email"
              />
            </div>
          </div>
        </template>
      </AppCard>
      <!--
        Setup MFA card hidden until the MFA flow is finished. When ready,
        delete this comment wrapper to re-enable. The SetupMfa modal,
        isSetuping state, and isMfaEnabled computed below are left in
        place so re-enabling is a one-step change.
      -->
      <!-- <AppCard
        v-if="type === 'edit'"
        :mode="mode"
        title="Setup MFA"
        :isCenter="false"
        :isSmallTitle="true"
      >
        <template #body>
          <AppButton
            v-if="!isMfaEnabled"
            title="Enable MFA"
            @action="isSetuping = true"
          />
          <AppButton v-else title="Disable MFA" />
        </template>
      </AppCard> -->
      <div
        class="grid gap-6 sm:grid-cols-1 sm:gap-4"
        :class="[type === 'edit' ? 'grid-cols-2' : '']"
      >
        <AppButton
          :title="
            type === 'edit' ? 'Update my information' : 'Add my information'
          "
          @action="handleContact"
        />
        <div v-if="type === 'edit'">
          <AppButton
            :isLink="true"
            :to="{ name: 'contact-set-password', params: { id: id } }"
            title="Update my password"
            type="secondary"
          />
        </div>
      </div>
    </div>
    <SetupMfa
      :mode="mode"
      :isSetuping="isSetuping"
      @closeSetupMfa="isSetuping = false"
    />
  </div>
</template>

<script>
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import Section from "@C/Common/Section.vue";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import SetupMfa from "@C/Twilio/SetupMfa.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "ContactActions",
  props: {
    type: {
      type: String,
      default: "edit",
    },
    mode: String,
  },
  components: {
    Section,
    AppCard,
    AppInput,
    AppSelect,
    AppCheckbox,
    SetupMfa,
    AppButton,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  data() {
    return {
      id: "",
      contactData: {
        address1: "",
        address2: "",
        administrator: false,
        billing_contact: false,
        board_end_date: "",
        board_start_date: "",
        board_type: null,
        chair: null,
        city: "",
        contact_id: "",
        country: "",
        default_email_is_buy: true,
        email: "",
        email_is_valid: true,
        facebook_url: "",
        fax: "",
        first_name: "",
        home_phone: "",
        im_name: "",
        last_login_date: "",
        last_mfr_id: null,
        last_name: "",
        linkedin_url: "",
        mobile_phone: "",
        notes: null,
        on_vacation: false,
        other_phone: "",
        press: false,
        primary_contact: true,
        rec_leads: true,
        rec_news_letter: true,
        state: "",
        title: "",
        username: "",
        work_phone: "",
        zip: "",
      },
      memberData: {
        member_id: "",
        toll_free_phone: "",
      },
      states: [],
      cities: [],
      debounceTimer: null,
      isSetuping: false,
    };
  },
  created() {
    if (this.type === "edit") {
      this.id = this.$route.params.id;
      if (this.contact.contact_id !== this.id) {
        this.toastStore.show("Contact not found", "error");
        this.$router.push({ name: "home" });
      }
    }
  },
  mounted() {
    if (this.type === "edit") {
      this.getContact();
      this.memberData = this.member;
    } else {
      this.memberData = {
        member_id: this.member.member_id,
        toll_free_phone: this.member.toll_free_phone,
      };
    }
  },
  methods: {
    async handleContact() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact: this.contactData,
        member: this.memberData,
      };
      try {
        const { data } = await ApiRequest().post(`/contact`, payload);
        if (data) {
          this.toastStore.show(
            `Contact ${
              this.type === "edit" ? "updated" : "addded"
            } successfully!`,
            "success",
          );
          if (this.type === "add") {
            this.$router.push({ name: "settings" });
          }
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getContact() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact/${this.id}`);
        if (data && data.contact) {
          this.contactData = data.contact;
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getLocations() {
      if (!this.contactData.country || !this.contactData.zip) {
        this.states = [];
        this.cities = [];
        return;
      }
      try {
        const payload = {
          country_code: this.$getCountryCode(
            this.countries,
            this.contactData.country,
          ),
          postal_code: this.contactData.zip,
        };
        const response = await fetch("/public_api/postal_codes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          this.toastStore.show("Failed to fetch location data", "error");
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show("No location data found", "error");
          this.states = [];
          this.cities = [];
          return;
        }
        const statesMap = new Map();
        const citiesMap = new Map();
        data.forEach((item) => {
          const source = item._source;
          if (source.admin_code1) {
            statesMap.set(source.admin_code1, {
              code: source.admin_code1,
              name: source.admin_name1 || source.admin_code1,
            });
          }
          if (source.place_name) {
            citiesMap.set(source.place_name, {
              name: source.place_name,
            });
          }
        });
        this.states = Array.from(statesMap.values());
        this.cities = Array.from(citiesMap.values());
      } catch (e) {
        this.handleError(e);
        this.states = [];
        this.cities = [];
      }
    },
    debouncedGetLocations() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.getLocations();
      }, 500);
    },
  },
  computed: {
    loggedInUser() {
      return this.userStore.user;
    },
    contact() {
      return this.loggedInUser.contact;
    },
    member() {
      return this.loggedInUser.member;
    },
    isMfaEnabled() {
      return this.loggedInUser?.contact_auth?.mfa_enabled;
    },
    countries() {
      return this.countriesStore.countries;
    },
  },
  watch: {
    "contactData.country": function (newVal) {
      if (newVal && this.contactData.zip) {
        this.debouncedGetLocations();
      }
    },
    "contactData.zip": function (newVal) {
      if (newVal && this.contactData.country) {
        this.debouncedGetLocations();
      }
    },
  },
};
</script>
